import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useIslands } from '@/lib/islands';
import { useI18n } from '@/lib/i18n';
import { bajty, fetchRuchAgenta } from '@/lib/ruch-agenta';

/**
 * ZUŻYCIE DANYCH PRZEZ AGENTÓW — zakładka w „Logi i statystyki".
 *
 * ⚠️ PO CO TO JEST, W JEDNYM ZDANIU: agent na maszynie z LTE zjadł u klienta pakiet 150 GB w dwa
 * tygodnie i nikt tego nie widział, dopóki nie skończył się pakiet. Ten ekran jest tą liczbą,
 * której wtedy nie było.
 *
 * ⚠️ ROZBICIE PO RODZAJU JEST TU WAŻNIEJSZE OD SUMY. Suma mówi „jest dużo"; rodzaj mówi „to kopie
 * bazy" albo „to odpytywanie o zadania" — a to dwie zupełnie różne naprawy. Przy tamtej awarii
 * właśnie ta kolumna wskazałaby winowajcę w pięć sekund.
 */

const OKNA = [1, 7, 30, 90] as const;

type Kierunek = 'in' | 'out';

/**
 * KIERUNEK RUCHU — strzałka, kolor i wartość razem.
 *
 * ⚠️ STRZAŁKA I NAPIS NIOSĄ ZNACZENIE, KOLOR JE TYLKO WZMACNIA. Odwrotnie byłoby wykluczeniem:
 * przy dwóch kolorach różniących się odcieniem (a nie jasnością) sam kolor nie odpowiada na
 * pytanie „w którą stronę", jeśli ktoś ich nie rozróżnia. Dlatego zostają oba napisy.
 *
 * ⚠️ `sky`/`emerald`, NIE `--success`/`--destructive`. Kierunek ruchu nie jest OCENĄ — 3 MB
 * w dół to nie „dobrze", a 1 MB w górę to nie „źle". Kolor z semantyki stanu podpowiadałby
 * werdykt, którego tu nie ma; werdykt jest jeden i stoi osobno przy progu alarmu.
 */
const IKONA = { in: ArrowUp, out: ArrowDown } as const;
const KOLOR: Record<Kierunek, string> = {
  in: 'text-sky-600 dark:text-sky-400',
  out: 'text-emerald-600 dark:text-emerald-400',
};

function Ruch({ kierunek, bajtow, mocny = false }: { kierunek: Kierunek; bajtow: number; mocny?: boolean }) {
  const { t } = useI18n();
  const Ikona = IKONA[kierunek];
  return (
    <span className={`inline-flex items-baseline gap-1.5 ${mocny ? 'text-base' : 'text-xs'}`}>
      <Ikona className={`${mocny ? 'size-4' : 'size-3'} shrink-0 self-center ${KOLOR[kierunek]}`} aria-hidden="true" />
      <span className={`tabular-nums ${mocny ? 'font-semibold' : ''}`}>{bajty(bajtow)}</span>
      <span className="text-xs font-normal text-muted-foreground">
        {t(kierunek === 'in' ? 'traffic.in' : 'traffic.out')}
      </span>
    </span>
  );
}

/** Sama strzałka do nagłówka kolumny — wartości w tabeli zostają czarne, żeby dały się czytać wzrokiem w dół. */
function StrzalkaNaglowka({ kierunek }: { kierunek: Kierunek }) {
  const Ikona = IKONA[kierunek];
  return <Ikona className={`inline-block size-3 shrink-0 align-text-bottom ${KOLOR[kierunek]}`} aria-hidden="true" />;
}

export function ZuzycieDanych() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const [dni, setDni] = useState<number>(7);

  const { data, isLoading } = useQuery({
    queryKey: ['ruch-agenta', groupId, dni],
    queryFn: () => fetchRuchAgenta(groupId!, dni),
    enabled: Boolean(groupId),
  });

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('traffic.pickIsland')}</p>;
  if (isLoading || !data) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  const razem = data.suma.bytesIn + data.suma.bytesOut;
  /** Średnia na dobę z wybranego okna — to ta liczba, którą porównuje się z pakietem operatora. */
  const naDobe = razem / Math.max(1, data.dni);
  const ponadProg = data.limitMbNaDobe > 0 && naDobe / 1024 / 1024 > data.limitMbNaDobe;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {OKNA.filter((d) => d <= data.dniPrzechowywania).map((d) => (
          <Button key={d} size="sm" variant={dni === d ? 'default' : 'outline'} onClick={() => setDni(d)}>
            {d === 1 ? t('traffic.day') : `${d} ${t('traffic.days')}`}
          </Button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">
          {t('traffic.retention')}: {data.dniPrzechowywania} {t('traffic.days')}
        </span>
      </div>

      {/*
        ⚠️ KAFELKI MÓWIĄ TO, CO SIĘ POTEM PORÓWNUJE Z FAKTURĄ — więc obok liczby stoi zdanie
        o tym, czego ona NIE zawiera. Bez tego ktoś porówna ją z rachunkiem operatora i uzna,
        że licznik kłamie, choć różnica to narzut TLS i TCP/IP.
      */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('traffic.total')}</p>
          <p className="text-2xl font-semibold tabular-nums">{bajty(razem)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Ruch kierunek="in" bajtow={data.suma.bytesIn} mocny />
            <Ruch kierunek="out" bajtow={data.suma.bytesOut} mocny />
          </div>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('traffic.perDay')}</p>
          <p className={`text-2xl font-semibold tabular-nums ${ponadProg ? 'text-destructive' : ''}`}>{bajty(naDobe)}</p>
          <p className="text-xs text-muted-foreground">
            {data.limitMbNaDobe > 0 ? `${t('traffic.threshold')}: ${data.limitMbNaDobe} MB` : t('traffic.noThreshold')}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('traffic.requests')}</p>
          <p className="text-2xl font-semibold tabular-nums">{data.suma.requests.toLocaleString('pl')}</p>
          <p className="text-xs text-muted-foreground">{t('traffic.tlsNote')}</p>
        </div>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium">{t('traffic.perNode')}</h2>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-2">{t('traffic.node')}</th>
                <th className="p-2 text-right">
                  <StrzalkaNaglowka kierunek="in" /> {t('traffic.in')}
                </th>
                <th className="p-2 text-right">
                  <StrzalkaNaglowka kierunek="out" /> {t('traffic.out')}
                </th>
                <th className="p-2 text-right">{t('traffic.total')}</th>
                <th className="p-2 text-right">{t('traffic.requests')}</th>
              </tr>
            </thead>
            <tbody>
              {data.wezly.map((w) => (
                <tr key={w.nodeId} className="border-t">
                  <td className="p-2">
                    {w.nazwa ?? w.nodeId}
                    {w.nazwa ? <span className="ml-2 text-xs text-muted-foreground">{w.nodeId}</span> : null}
                  </td>
                  <td className="p-2 text-right tabular-nums">{bajty(w.bytesIn)}</td>
                  <td className="p-2 text-right tabular-nums">{bajty(w.bytesOut)}</td>
                  <td className="p-2 text-right font-medium tabular-nums">{bajty(w.bytesIn + w.bytesOut)}</td>
                  <td className="p-2 text-right tabular-nums">{w.requests.toLocaleString('pl')}</td>
                </tr>
              ))}
              {data.wezly.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-3 text-center text-muted-foreground">
                    {t('traffic.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium">{t('traffic.perKind')}</h2>
        <div className="flex flex-wrap gap-2">
          {data.rodzaje.map((r) => {
            const suma = r.bytesIn + r.bytesOut;
            const udzial = razem > 0 ? Math.round((suma / razem) * 100) : 0;
            return (
              <div key={r.kind} className="rounded-lg border px-3 py-2">
                <p className="text-xs text-muted-foreground">{t(`traffic.kind.${r.kind}`)}</p>
                <p className="font-medium tabular-nums">
                  {bajty(suma)} <Badge variant="neutral">{udzial}%</Badge>
                </p>
                {/*
                  ⚠️ ROZBICIE NA KIERUNKI JEST TU NAJWAŻNIEJSZE Z CAŁEGO EKRANU. „Aktualizacje 40 MB"
                  nic nie mówi, dopóki nie widać, że to 40 MB W DÓŁ — a to zupełnie inna naprawa niż
                  40 MB w górę na tym samym kluczu (tamto znaczyłoby, że agent coś WYSYŁA).
                */}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <Ruch kierunek="in" bajtow={r.bytesIn} />
                  <Ruch kierunek="out" bajtow={r.bytesOut} />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {r.requests.toLocaleString('pl')} {t('traffic.requestsShort')}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <p className="text-xs text-muted-foreground">{t('traffic.footnote')}</p>
    </div>
  );
}
